import React, {
  useMemo, useState, forwardRef, useEffect
} from 'react';
import { Form, Title, Spacer } from 'components/atoms';
import { Formik, Field } from 'formik';
import {
  FieldArrayDndFormik, FieldCustom, FieldSelect, FieldMarkdown,
} from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use'
import { useFormikForwardRef } from 'hooks'
import queryString from 'query-string';
import { queryLocations } from 'services/requests';
import { toast } from 'react-toastify';
import { Toast } from 'containers';
import { generateKey } from 'services/generators';
import { linkRegex } from 'services/constants'

const SocialItemSchema = Yup.object().shape({
  type: Yup.string().required('Campo obrigatório'),
  url: Yup.string()
    .matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
    .required('Campo obrigatório')
});

// eslint-disable-next-line react/prop-types
const SocialItemForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  return (
    <Formik
      ref={formRef}
      initialValues={initialValues}
      validationSchema={SocialItemSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit({
          ...values
        });
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          name="type"
          title="Rede Social"
          component={FieldSelect}
          options={[
            { value: 'instagram', label: 'Instagram' },
            { value: 'twitter', label: 'Twitter' },
            { value: 'facebook', label: 'Facebook' },
            { value: 'linkedIn', label: 'LinkedIn' },
            { value: 'youtube', label: 'Youtube' }
          ]}
        />
        <Field
          title="Link"
          name="url"
          info="Link para a rede social"
          component={FieldCustom}
        />

      </Form>
    </Formik>
  )
})

const Schema = Yup.object().shape({
  title: Yup.string()
    .required('Campo obrigatório'),
  lon: Yup.string().required('Campo obrigatório'),
  lat: Yup.string().required('Campo obrigatório'),
  z: Yup.string().required('Campo obrigatório'),
  offset: Yup.string().required('Campo obrigatório'),
  locationId: Yup.string().nullable(),
  main: Yup.string()
    .matches(/^(?:[^\r\n]*(\r\n?|\n)){0,2}[^\r\n]*$/, 'Máximo 2 linhas')
    .max(280, 'Máximo de 280 caracteres'),
  data: Yup.string()
    .matches(/^(?:[^\r\n]*(\r\n?|\n)){0,2}[^\r\n]*$/, 'Máximo 2 linhas')
    .max(280, 'Máximo de 280 caracteres'),
  social: Yup.array().of(SocialItemSchema),
});

const computeOptions = (opts, valueKey = 'id', labelKey = 'name') => opts.map((option) => ({
  value: option[valueKey],
  label: option[labelKey],
  original: option
}))

const SubWebContactsEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps)
  const [locations, setLocations] = useState([]);
  const [isLoading, setLoading] = useState(false)

  const initialValues = useMemo(() => {
    const {
      data, main, social, title, mapUrl, locationId = null, offset = 6
    } = initialProps;

    const { q, z } = queryString.parse(mapUrl)
    const [lat, lon] = q.split(',');

    return {
      data, main, social, title, lat, lon, z, offset, locationId
    };
  }, [initialProps]);

  // todo add select for locations
  useEffect(() => {
    const getLocationsData = async () => {
      try {
        setLoading(true)
        const { data: { data: locData } } = await queryLocations({ completeList: true });
        setLocations(computeOptions(locData));
        setLoading(false)
      } catch (err) {
        setLoading(false)
        toast.error(
          <Toast
            type="error"
            title="Erro a carregar formulário de Contactos"
            description={err.message}
          />
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
    getLocationsData();
  }, []);


  const handleFormChange = newValues => {
    const {
      z, offset, lat, lon, locationId, ...values
    } = newValues

    const computedValues = {
      lat,
      lon,
      z,
      offset,
      ...values
    }

    // copy variable values
    let latNew = lat;
    let lonNew = lon;
    let locationIdNew = locationId;

    // get old values from url
    const { q } = queryString.parse(initialProps.mapUrl)
    const [latPrevious, lonPrevious] = q.split(',');

    // check if location id changed from  Previous, if so get new values from locations
    if (locationId !== undefined
      && locationId !== null
      && locationId !== initialValues.locationId) {
      const [latBD, lonBD] = locations[locationId - 1].original.coordenates.split(', ');
      latNew = latBD;
      lonNew = lonBD;
    // check if coordenates input change, if so reset locations dropdown
    } else if (latPrevious !== lat || lonPrevious !== lon) {
      locationIdNew = null
    }

    // calculate offset based on zoom
    const offsetCalc = parseFloat(lonNew) + offset * (Math.abs(offset) ** Number(30 / z)) / (z ** 4)

    // get new google url
    const newMapUrl = `https://maps.google.com/maps?output=embed&q=${latNew},${lonNew}&ll=${latNew},${offsetCalc}&z=${z || 20}`

    const newProps = {
      ...initialProps,
      ...values,
      mapUrl: newMapUrl,
      locationId: locationIdNew,
      offset
    }

    const isValid = Schema.isValidSync(computedValues)
    if (isValid) setEditorProps(newProps)
  };


  useDebounce(() => {
    onChange(editorProps);
  }, 300,
  [editorProps]
  )

  if (isLoading) {
    return (
      <div>
      A carregar...
      </div>
    )
  }


  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Schema}
      validate={handleFormChange}
      enableReinitialize
    >
      <Form>

        <Title as="h3">Conteúdos</Title>
        <Field
          title="Título"
          name="title"
          info="Uma frase para secção"
          component={FieldCustom}
          maxLetters={120}
        />
        <Field
          title="Secção 1"
          name="main"
          info="Telefone, morada, etc"
          component={FieldMarkdown}
        />
        <Field
          title="Secção 2"
          name="data"
          info="Telefone, morada, etc"
          component={FieldMarkdown}
        />
        <Field
          title="Redes sociais"
          name="social"
          component={FieldArrayDndFormik}
          columns={[
            {
              accessor: 'type',
            },
            {
              accessor: 'url',
            }
          ]}
          rowKey={(item) => generateKey(item.type)}
          ItemForm={SocialItemForm}
          shouldHideHeaders
        />
        <Spacer top="xl" />
        <Title as="h3">Mapa</Title>
        <Field
          title="Localização Predefinida"
          name="locationId"
          placeholder="Pode selecionar uma localização existente"
          info="Pode usar a lista para obter as coordenadas de uma lista pré-definida"
          component={FieldSelect}
          options={locations}
        /> 
        <Field
          title="Latitude"
          name="lat"
          info="Ex: 40.629560"
          component={FieldCustom}
          type="number"
        />
        <Field
          title="Longitude"
          name="lon"
          info="Ex: -8.656936"
          component={FieldCustom}
          type="number"
        />
        <Field
          title="Zoom"
          name="z"
          info={`${initialValues.z}`}
          component={FieldCustom}
          type="range"
          max="21"
          min="1"
        />
        <Field
          title="Offset"
          name="offset"
          component={FieldCustom}
          info={`${initialValues.offset}`}
          type="range"
          max="20"
          min="0"
        />

      </Form>
    </Formik>
  );
};
SubWebContactsEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    source: PropTypes.string,
    mapUrl: PropTypes.string,
  }),
};

SubWebContactsEditor.defaultProps = {
  initialProps: {}
};

export default SubWebContactsEditor;
